import { Alert, Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import type { PackagingChange, RetireWarning } from '../../staff/changeSummary';
import type { PublishReport, WriteOutcome } from '../../staff/publishPackaging';

interface PublishDialogProps {
  opened: boolean;
  changes: PackagingChange[];
  report: PublishReport | null;
  busy?: boolean;
  warnings?: RetireWarning[];
  discardableItems?: string[];
  onDiscard?: (id: string) => void;
  onPublish: () => void;
  onClose: () => void;
}

// Customer-terms copy for a non-blocking retire warning.
function warningSentence(w: RetireWarning): string {
  if (w.relation === 'requiresTier') return `${w.referencedBy} still requires ${w.retired} — new orgs cannot reach it`;
  if (w.relation === 'confers') return `${w.referencedBy} still confers ${w.retired} — that trial becomes unactionable`;
  return `${w.retired} is the only tier granting ${w.feature} — retiring it makes ${w.feature} unobtainable for new orgs`;
}

function capitalise(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

// Customer-terms sentence for a single packaging change. Slugs stand for themselves (module,
// feature, offer, and the target tier of a requirement change); a tier used as the subject of
// "Orgs on <tier>" reads naturally capitalised.
function sentence(change: PackagingChange): string {
  switch (change.kind) {
    case 'tier-added':
      return `New tier ${change.tier}`;
    case 'module-added':
      return `New module ${change.module}`;
    case 'offer-added':
      return `New trial offer ${change.offer}`;
    case 'feature-introduced':
      return `New feature ${change.feature}`;
    case 'feature-granted':
      return `Orgs on ${capitalise(change.tier)} gain ${change.feature}`;
    case 'feature-revoked':
      return `Orgs on ${capitalise(change.tier)} lose ${change.feature}`;
    case 'module-feature-added':
      return `${change.module} gains ${change.feature}`;
    case 'module-feature-removed':
      return `${change.module} loses ${change.feature}`;
    case 'module-requirement-changed':
      if (change.to === null) return `${change.module} no longer requires a tier`;
      if (change.from === null) return `${change.module} now requires ${change.to}`;
      return `${change.module} now requires ${change.to} (was ${change.from})`;
    case 'tier-retired':
      return `Tier ${capitalise(change.tier)} retired — no new orgs can be granted it; current holders keep their access`;
    case 'tier-restored':
      return `Tier ${capitalise(change.tier)} restored — available to grant again`;
    case 'module-retired':
      return `Module ${change.module} retired — no new orgs can be granted it; current holders keep their access`;
    case 'module-restored':
      return `Module ${change.module} restored — available to grant again`;
    case 'offer-retired':
      return `Trial offer ${change.offer} retired`;
    case 'offer-changed':
      return `Trial offer ${change.offer} updated`;
  }
}

function changeKey(change: PackagingChange): string {
  return Object.values(change).join(':');
}

// Status-specific copy per outcome — 409 and 412 are both "conflict" but need different next
// steps (reload to see the pre-existing item vs. reload to pick up someone else's edit).
function outcomeCopy(id: string, outcome: WriteOutcome): string {
  switch (outcome.kind) {
    case 'published':
      return 'Published';
    case 'rejected':
      return outcome.reason ? `Rejected: ${outcome.reason}` : 'Rejected';
    case 'unaudited':
      return 'Saved, not recorded to the audit log — retry is safe';
    case 'conflict':
      return outcome.status === 412
        ? 'Changed since you loaded it — reload'
        : `A ${id} already exists — reload to see it, or rename`;
    case 'unreachable':
      return "Couldn't reach the service — nothing was saved, try again";
  }
}

function outcomeColor(outcome: WriteOutcome): string {
  if (outcome.kind === 'published') return 'var(--status-success)';
  if (outcome.kind === 'unaudited') return 'var(--status-warning)';
  return 'var(--status-danger)';
}

export function PublishDialog({
  opened, changes, report, busy = false, warnings = [], discardableItems = [], onDiscard, onPublish, onClose,
}: Readonly<PublishDialogProps>) {
  const hasFailure = report?.some((row) => row.outcome.kind !== 'published') ?? false;

  return (
    <Modal opened={opened} onClose={onClose} title="Publish packaging changes" centered size={560}>
      <Stack gap="lg">
        <Stack gap={6}>
          <Text fz="sm" fw={700} c="var(--text-default)">Changes</Text>
          {changes.length === 0 ? (
            <Text fz="sm" c="var(--text-subtle)">No changes to publish.</Text>
          ) : (
            <List size="sm" spacing={4}>
              {changes.map((change) => (
                <List.Item key={changeKey(change)}>{sentence(change)}</List.Item>
              ))}
            </List>
          )}
        </Stack>

        {warnings.length > 0 && (
          <Alert role="status" color="var(--status-warning)" variant="light">
            <Text fz="sm" fw={700} c="var(--status-warning)">Heads up</Text>
            <List size="sm">
              {warnings.map((w) => <List.Item key={`${w.retired}:${w.referencedBy}`}>{warningSentence(w)}</List.Item>)}
            </List>
          </Alert>
        )}

        {onDiscard && discardableItems.length > 0 && (
          <Stack gap={6}>
            <Text fz="sm" fw={700} c="var(--text-default)">Back out a change</Text>
            <List size="sm" spacing={4} listStyleType="none">
              {discardableItems.map((id) => (
                <List.Item key={id}>
                  <Group justify="space-between">
                    <Text component="span" fz="sm">{id}</Text>
                    <Button size="compact-xs" variant="subtle" color="var(--status-danger)"
                      aria-label={`Discard ${id}`} onClick={() => onDiscard(id)}>Discard</Button>
                  </Group>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        {report && (
          <Stack gap={6}>
            {hasFailure && (
              <Alert role="alert" color="var(--status-danger)" variant="light"
                style={{ borderColor: 'var(--status-danger-background)', '--alert-bg': 'var(--status-danger-background)' }}>
                <Text c="var(--status-danger)" size="sm">Some writes did not complete — reload before republishing.</Text>
              </Alert>
            )}
            <Text fz="sm" fw={700} c="var(--text-default)">Results</Text>
            <List size="sm" spacing={4} listStyleType="none">
              {report.map((row) => (
                <List.Item key={row.id}>
                  <Text component="span" fz="sm" c="var(--text-default)">{row.id}: </Text>
                  <Text component="span" fz="sm" c={outcomeColor(row.outcome)}>{outcomeCopy(row.id, row.outcome)}</Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        <Group justify="flex-end" gap={10}>
          <Button variant="default" onClick={onClose}>Close</Button>
          <Button onClick={onPublish} disabled={changes.length === 0 || busy} loading={busy}>
            Publish
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
